import { useCallback, useMemo } from 'react';
import { Cell, Checkbox } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { type BaseRowData } from '@adv-frontend/data-provider';
import { type ColumnCellTemplateProps } from '@adv-frontend/grid';
import { getSelectableSubRowsIds } from './lib/getSelectableSubRowsIds';
import { type GridRow as GridRowData, type GridValidatorOptions, type GridValidatorType } from '../../lib/types';
import { useRowValidator } from '../../lib/useRowValidator';

export interface CellSelectProps<RowData extends BaseRowData = GridRowData>
    extends ColumnCellTemplateProps<RowData>,
        TestProps {
    label: string;
    hasParentRowSelectAllChildren?: boolean;
    toggleWithSubRows?: boolean;
    gridValidatorType?: GridValidatorType;
    gridValidatorOptions?: GridValidatorOptions;
    disableNewChecked?: boolean;
}

export const CellSelect = <RowData extends BaseRowData = GridRowData>(props: CellSelectProps<RowData>) => {
    const {
        'data-testid': dataTestId = 'CellSelect',
        row,
        label,
        selectionManager,
        hasParentRowSelectAllChildren = false,
        toggleWithSubRows = false,
        gridValidatorType,
        gridValidatorOptions,
        data,
        table,
        disableNewChecked = false,
    } = props;
    const { getCanExpand, id } = row;

    const subRowsIds = useMemo(() => getSelectableSubRowsIds(row.subRows), [row.subRows]);

    const isSelected = selectionManager?.useIsSelected(id) ?? false;
    const isAllSubRowsSelected = selectionManager?.useIsEveryRowSelected(subRowsIds) ?? false;
    const isSomeSubRowsSelected = selectionManager?.useIsSomeRowSelected(subRowsIds) ?? false;

    const isCellSelected = hasParentRowSelectAllChildren && getCanExpand() ? isAllSubRowsSelected : isSelected;
    const isIndeterminate = hasParentRowSelectAllChildren
        ? getCanExpand() && !isAllSubRowsSelected && isSomeSubRowsSelected
        : false;

    const handleChange = useCallback(() => {
        selectionManager?.toggleRow(id);

        if (hasParentRowSelectAllChildren) {
            if (isCellSelected) {
                if (toggleWithSubRows) {
                    subRowsIds.forEach((subRowId) => {
                        selectionManager?.deselectRow(subRowId);
                    });
                }
            } else if (toggleWithSubRows) {
                subRowsIds.forEach((subRowId) => {
                    selectionManager?.selectRow(subRowId);
                });
            }

            return;
        }

        if (toggleWithSubRows) {
            if (row.parentId) {
                const parentSubRows = table.getRowModel().rows.find((r) => r.id === row.parentId)?.subRows || [];

                if (selectionManager?.isEveryRowSelected(parentSubRows.map((subRow) => subRow.id))) {
                    selectionManager.selectRow(row.parentId);
                } else {
                    selectionManager?.deselectRow(row.parentId);
                }
            } else if (isSelected) {
                subRowsIds.forEach((subRowId) => {
                    selectionManager?.deselectRow(subRowId);
                });
            } else {
                selectionManager?.selectMultipleRows(subRowsIds);
            }
        }
    }, [
        hasParentRowSelectAllChildren,
        id,
        isCellSelected,
        isSelected,
        row.parentId,
        selectionManager,
        subRowsIds,
        table,
        toggleWithSubRows,
    ]);

    const rowValidator = useRowValidator(gridValidatorType, gridValidatorOptions);

    const rowStatus = useMemo(() => rowValidator(data).statusColor, [data, rowValidator]);

    return (
        <Cell data-testid={dataTestId} align="left" size="m" type="Checkbox" statusColor={rowStatus}>
            <Checkbox
                aria-label={label}
                size="xs"
                isDisabled={!selectionManager?.useIsSelectable(id) || (disableNewChecked && !isCellSelected)}
                isIndeterminate={isIndeterminate}
                isSelected={isCellSelected}
                onChange={handleChange}
            />
        </Cell>
    );
};
